import { useMutation, useQueryClient } from "@tanstack/react-query";
import { pathPickMutationApi } from "@/features/pathpick/api/mutation";
import { pathPickQueryApi } from "@/features/pathpick/api/query";
import { PathPickDisconnectProductsRequestBody } from "@/features/pathpick/types/request-body";
import { PathPickDisconnectProductsResponse } from "@/features/pathpick/types/response";
import useCompanyIdentity from "@/shared/hooks/useCompanyIdentity";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { refreshPathPickProducts } from "@/features/pathpick/utils/query-cache";

interface Variables {
  body: PathPickDisconnectProductsRequestBody;
  // 스토어의 원상품까지 삭제할지 — 기본은 연동만 해제
  deleteFromStore?: boolean;
}

// 작업 상태 조회 간격·상한 — 100개를 세 마켓에서 내리면 몇 분이 걸린다(2026-10-01 FIXS 한 요청 4~8분).
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30 * 60 * 1000;

// 서버가 작업을 실패로 끝냈거나 상한까지 끝나지 않았을 때 — 알 수 없는 오류 대신 해제 실패 안내를 띄운다.
class DisconnectJobFailedError extends Error {}

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

// 작업이 끝날 때까지 조회한다. 끝나면 동기 API 와 같은 { results } 를 돌려줘 호출부가 그대로 쓴다.
// 첫 조회에도 아직이면 onStillRunning 을 한 번 불러 '진행 중' 안내를 띄운다(금방 끝나는 건 조용히 넘긴다).
const waitForDisconnectJob = async (
  companyId: number,
  jobId: string,
  onStillRunning: () => void
): Promise<PathPickDisconnectProductsResponse> => {
  const deadline = Date.now() + POLL_TIMEOUT_MS;
  let notified = false;
  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS);
    const job = await pathPickQueryApi.getPathPickDisconnectJob(
      companyId,
      jobId
    );
    if (job.status === "COMPLETED") {
      return { results: job.results ?? [] };
    }
    if (job.status === "FAILED") {
      throw new DisconnectJobFailedError(job.errorMessage ?? "FAILED");
    }
    if (!notified) {
      notified = true;
      onStillRunning();
    }
  }
  throw new DisconnectJobFailedError("TIMEOUT");
};

// 연동 해제(단건/벌크 공용) mutation.
// 서버에 작업으로 접수하고 끝날 때까지 조회한다 — 한 번에 기다리면 30초 타임아웃에 걸렸다.
// 끝나면 상품별 성공 여부가 results로 내려오므로 부분 실패 안내는 호출부가 담당한다.
const useDisconnectPathPickProducts = () => {
  const { companyId } = useCompanyIdentity();
  const queryClient = useQueryClient();
  const { t } = useI18nHelper();
  const { error, info, add } = useAppToasts({ id: "GLOBAL" });

  const invalidateLists = () => {
    // 요청 내역/상품별 처리 내역 (["pathpick","requests",companyId] 프리픽스로 함께 갱신)
    queryClient.invalidateQueries({
      queryKey: ["pathpick", "requests", companyId],
    });
    // 상품의 채널별 요청 슬롯(requests.naverSync 등) 배지 반영
    // — 화면에 없는 상품 목록은 비워, 돌아왔을 때 배지가 뒤늦게 빠지지 않게 한다.
    refreshPathPickProducts(queryClient, companyId);
    // 전체 선택 대상(ids_only)과 "전체 n개 선택"의 n도 같은 변화를 따라간다.
    queryClient.invalidateQueries({
      queryKey: ["pathpick", "api-product-ids", companyId],
    });
    // GNB/내역 페이지의 상품별 처리 내역 카운트(request.productCount) 반영
    queryClient.invalidateQueries({
      queryKey: ["pathpick", "counts", companyId],
    });
    // 담은 상품 탭 배지 카운트 — 연동 해제된 상품이 등록 신청함에서 빠진다.
    queryClient.invalidateQueries({
      queryKey: ["pathpick", "basket", "counts", companyId],
    });
  };

  return useMutation({
    mutationFn: async ({ body, deleteFromStore }: Variables) => {
      const accepted = await pathPickMutationApi.requestPathPickDisconnectJob(
        companyId as number,
        body,
        deleteFromStore
      );
      return waitForDisconnectJob(companyId as number, accepted.jobId, () =>
        info(t("path_pick:sync_download_history.unsync_result.processing"))
      );
    },
    onSuccess: () => {
      invalidateLists();
    },
    onError: (err) => {
      if (err instanceof DisconnectJobFailedError) {
        // 실패로 끝나도 앞선 마켓은 이미 처리됐을 수 있어 목록은 새로 받는다.
        invalidateLists();
        // 서버 오류 원문은 사용자가 조치할 수 없어 해제 실패 안내로 묶는다.
        add({
          status: "error",
          message: t("path_pick:sync_download_history.unsync_result.failed"),
          id: "GLOBAL",
        });
        return;
      }
      error(err);
    },
  });
};

export default useDisconnectPathPickProducts;
